import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, ROLE_LABEL, type AdminUser, type Group, type Org } from "../../lib/admin";
import { AddUser, EditUser, StatusPill, TempPasswordNotice } from "./Users";
import { ORG_PROVIDERS } from "../../lib/basemap";
import { Drawer, Field, Notice, Pill, Toolbar, clean, fmtDate, fmtDateTime } from "./ui";

type Counts = Record<string, { devices: number; users: number }>;
const EMPTY = { name: "", type: "client", plan_id: "", billing_start: "", status: "active", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "" };

export default function Companies({ orgs, reload, groups, reloadGroups }: { orgs: Org[]; reload: () => Promise<void>; groups: Group[]; reloadGroups: () => Promise<void> }) {
  const [q, setQ] = useState("");
  const [counts, setCounts] = useState<Counts>({});
  const [edit, setEdit] = useState<Org | "new" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { // deep link from the Company page: /admin/companies?org=<id>
    const id = new URLSearchParams(window.location.search).get("org");
    const o = id && orgs.find((x) => x.id === id);
    if (o) setEdit(o);
  }, [orgs.length]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      const [{ data: d }, { data: m }] = await Promise.all([
        supabase.rpc("admin_devices"),
        supabase.from("memberships").select("org_id"),
      ]);
      const c: Counts = {};
      for (const r of ((d ?? []) as any[]).filter((x) => x.status !== "retired")) (c[r.org_id] ??= { devices: 0, users: 0 }).devices++;
      for (const r of m ?? []) (c[r.org_id] ??= { devices: 0, users: 0 }).users++;
      setCounts(c);
    })();
  }, [orgs]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return orgs.filter((o) => !s || [o.name, o.contact_name, o.contact_email, o.plan].some((v) => v?.toLowerCase().includes(s)));
  }, [orgs, q]);

  return (
    <section>
      <Toolbar>
        <h1>Companies</h1>
        <input className="asearch" placeholder="Search companies…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-primary" onClick={() => setEdit("new")}>+ Add company</button>
      </Toolbar>
      {msg && <Notice onClose={() => setMsg(null)}>{msg}</Notice>}
      <div className="atable-wrap">
        <table className="atable">
          <thead><tr><th>Company</th><th>Type</th><th>Status</th><th>Plan</th><th>Contact</th><th className="num">Devices</th><th className="num">Users</th><th className="num">Groups</th><th>Since</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} onClick={() => setEdit(o)}>
                <td><b>{o.name}</b></td>
                <td><Pill value={o.type} label={o.type === "internal" ? "Skyward" : "Client"} /></td>
                <td><Pill value={o.status} /></td>
                <td>{o.plan ?? "—"}</td>
                <td>{o.contact_name ?? "—"}<div className="sub">{o.contact_email ?? o.contact_phone ?? ""}</div></td>
                <td className="num mono">{counts[o.id]?.devices ?? 0}</td>
                <td className="num mono">{counts[o.id]?.users ?? 0}</td>
                <td className="num mono">{groups.filter((g) => g.org_id === o.id).length}</td>
                <td>{fmtDate(o.created_at)}</td>
              </tr>
            ))}
            {!rows.length && <tr className="empty"><td colSpan={9}>No companies found.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <CompanyForm org={edit === "new" ? null : edit} groups={groups} reloadGroups={reloadGroups} onClose={() => setEdit(null)}
          onSaved={async (m) => { setEdit(null); setMsg(m); await reload(); }} orgs={orgs} />
      )}
    </section>
  );
}

function CompanyForm({ org, groups, reloadGroups, onClose, onSaved, orgs }: {
  org: Org | null; groups: Group[]; reloadGroups: () => Promise<void>; onClose: () => void; onSaved: (msg: string) => void; orgs: Org[];
}) {
  const [f, setF] = useState(() => (org ? { ...EMPTY, ...Object.fromEntries(Object.entries(org).map(([k, v]) => [k, v ?? ""])) } : EMPTY) as typeof EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [maps, setMaps] = useState<string[]>(org?.map_providers ?? ["maptiler"]);
  const [plans, setPlans] = useState<{ id: string; name: string; price_per_device: number; active: boolean }[]>([]);
  useEffect(() => { supabase.from("plans").select("id,name,price_per_device,active").order("name").then(({ data }) => setPlans(data ?? [])); }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = clean({ name: f.name, type: f.type, plan_id: f.plan_id, billing_start: f.billing_start, status: f.status, contact_name: f.contact_name,
        contact_email: f.contact_email, contact_phone: f.contact_phone, address: f.address, notes: f.notes, map_providers: maps });
      if (org) await adminApi("PATCH", `orgs/${org.id}`, body);
      else await adminApi("POST", "orgs", body);
      onSaved(org ? `Saved ${f.name}.` : `Added ${f.name}. Next: add its devices and users.`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title={org ? org.name : "Add company"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="org-form" disabled={busy}>{busy ? "Saving…" : "Save"}</button></>}>
      <form id="org-form" className="aform" onSubmit={save}>
        <Field label="Company name" wide><input value={f.name} onChange={set("name")} required maxLength={120} /></Field>
        <Field label="Type">
          <select value={f.type} onChange={set("type")}>
            <option value="client">Client</option>
            <option value="internal">Skyward (internal)</option>
          </select>
        </Field>
        <Field label="Status" hint={f.status === "suspended" ? "Users keep access for now; device suspension comes with billing (P5b)." : undefined}>
          <select value={f.status} onChange={set("status")}>
            <option value="active">Active</option><option value="suspended">Suspended</option><option value="closed">Closed</option>
          </select>
        </Field>
        <Field label="Plan" hint="Billed per active device per month. Manage plans under Admin › Plans.">
          <select value={f.plan_id} onChange={set("plan_id")}>
            <option value="">No plan</option>
            {plans.filter((p) => p.active || p.id === f.plan_id).map((p) => (
              <option key={p.id} value={p.id}>{p.name} · FJD {Number(p.price_per_device).toFixed(2)}/device incl. VAT{p.active ? "" : " (inactive)"}</option>
            ))}
          </select>
        </Field>
        <Field label="Billing start"><input type="date" value={f.billing_start} onChange={set("billing_start")} /></Field>
        <Field label="Contact person"><input value={f.contact_name} onChange={set("contact_name")} maxLength={120} /></Field>
        <Field label="Contact email"><input type="email" value={f.contact_email} onChange={set("contact_email")} /></Field>
        <Field label="Contact phone"><input value={f.contact_phone} onChange={set("contact_phone")} maxLength={40} /></Field>
        <Field label="Address" wide><input value={f.address} onChange={set("address")} maxLength={300} /></Field>
        <Field label="Internal notes" wide><textarea value={f.notes} onChange={set("notes")} rows={3} maxLength={2000} /></Field>
        <fieldset className="wide aradio">
          <legend>Map layers this company can use</legend>
          <label><input type="checkbox" checked disabled /><span>Streets (free, always on)</span></label>
          {ORG_PROVIDERS.map(([k, l]) => (
            <label key={k}><input type="checkbox" checked={maps.includes(k)} onChange={(e) => setMaps(e.target.checked ? [...maps, k] : maps.filter((x) => x !== k))} />
              <span>{l}{k.startsWith("google") && <span className="sub"> — billed per use by Google</span>}</span></label>
          ))}
        </fieldset>
        {err && <div className="error wide">{err}</div>}
      </form>
      {org && <CompanyUsers org={org} orgs={orgs} groups={groups} />}
      {org && <GroupsEditor org={org} groups={groups.filter((g) => g.org_id === org.id)} reload={reloadGroups} />}
    </Drawer>
  );
}

function GroupsEditor({ org, groups, reload }: { org: Org; groups: Group[]; reload: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="aactions groups-ed">
      <h3>Device groups</h3>
      <p className="sub">Organise this company's vehicles (e.g. "Trucks", "Nadi depot"). Users can be limited to certain groups.</p>
      <ul>
        {groups.map((g) => (
          <li key={g.id}>
            <span>{g.name}</span>
            <button className="btn-ghost small" disabled={busy} onClick={() => {
              const n = prompt("Rename group", g.name);
              if (n && n.trim() && n.trim() !== g.name) run(() => adminApi("PATCH", `groups/${g.id}`, { name: n.trim() }));
            }}>Rename</button>
            <button className="btn-ghost small danger" disabled={busy} onClick={() => {
              if (confirm(`Delete group "${g.name}"? Its devices stay, just without a group.`)) run(() => adminApi("DELETE", `groups/${g.id}`));
            }}>Delete</button>
          </li>
        ))}
        {!groups.length && <li className="sub">No groups yet.</li>}
      </ul>
      <form className="group-add" onSubmit={(e) => { e.preventDefault(); if (name.trim()) run(async () => { await adminApi("POST", "groups", { org_id: org.id, name: name.trim() }); setName(""); }); }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name" maxLength={60} />
        <button className="btn-ghost" disabled={busy || !name.trim()}>Add group</button>
      </form>
      {err && <div className="error">{err}</div>}
    </div>
  );
}

/** Users tied to this company, with quick edit / invite (same forms as Admin → Users). */
function CompanyUsers({ org, orgs, groups }: { org: Org; orgs: Org[]; groups: Group[] }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<AdminUser | "new" | null>(null);
  const [msg, setMsg] = useState<{ text: string; password?: string; warn?: boolean } | null>(null);
  const load = async () => {
    try { setUsers((await adminApi<AdminUser[]>("GET", "users")).filter((u) => [...(u.org_ids ?? [u.org_id]), ...(u.client_org_ids ?? [])].includes(org.id))); setErr(null); }
    catch (e: any) { setErr(e.message); }
  };
  useEffect(() => { load(); }, [org.id]); // eslint-disable-line
  const done = async (text: string, password?: string, warn?: boolean) => { setEdit(null); setMsg({ text, password, warn }); await load(); };
  return (
    <div className="aactions co-users">
      <div className="co-head">
        <h3>Users <span className="sub">{users ? users.length : ""}</span></h3>
        <button className="btn-ghost small" onClick={() => setEdit("new")}>+ Invite user</button>
      </div>
      {msg && <TempPasswordNotice {...msg} onClose={() => setMsg(null)} />}
      {err && <div className="error">{err}</div>}
      <ul className="co-user-list">
        {users === null && !err && <li className="sub">Loading…</li>}
        {users?.map((u) => (
          <li key={u.id} onClick={() => setEdit(u)} role="button">
            <div><b>{u.full_name ?? u.email}</b><div className="sub mono">{u.email}</div></div>
            <span>{u.role ? ROLE_LABEL[u.role] : "—"}{u.group_scope.length > 0 && <span className="sub"> · limited</span>}</span>
            <StatusPill u={u} />
            <span className="sub">{u.last_sign_in_at ? fmtDateTime(u.last_sign_in_at) : "never signed in"}</span>
          </li>
        ))}
        {users && !users.length && <li className="sub">No users yet — invite the first one.</li>}
      </ul>
      {edit === "new" && <AddUser orgs={orgs} groups={groups} defaultOrg={org.id} onClose={() => setEdit(null)} onDone={done} />}
      {edit && edit !== "new" && <EditUser user={edit} orgs={orgs} groups={groups} onClose={() => setEdit(null)} onDone={done} />}
    </div>
  );
}
